/**
 * Symphony GROUP CHAT — the client-side state behind ticks, "Seen by", typing and online / last seen (design §21).
 * A small store of its own (not `group-chat-api`'s), so a typing frame re-renders only what reads typing. The rules
 * are `lib/group-receipts.ts`; this file only holds what they act on and moves data.
 *
 * Every call made from here is a fire-and-forget hint: failures and refusals (PERMISSION_DENIED, FEATURE_OFF) are
 * ignored, never shown.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import type { MessageAuthor } from './group-chat';
import { groupChatAction, groupChatBeacon } from './group-chat-api';
import {
  applyTypingFrame,
  DeliveredAcker,
  dropTypingForAuthor,
  mergeReceiptFrame,
  nextTypingExpiry,
  parsePresence,
  pruneTyping,
  withPresence,
  type GroupReceipts,
  type PresenceInfo,
  type PresenceMap,
  type ReceiptFrame,
  type TypingEntry,
  type TypingFrame,
} from './group-receipts';

interface LiveState {
  receipts: Readonly<Record<string, GroupReceipts | null>>;
  typing: Readonly<Record<string, readonly TypingEntry[]>>;
  presence: PresenceMap;
}
const EMPTY_TYPING: readonly TypingEntry[] = [];
let state: LiveState = { receipts: {}, typing: {}, presence: {} };
const listeners = new Set<() => void>();
function setState(next: LiveState) {
  if (next === state) return;
  state = next;
  listeners.forEach((l) => l());
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
/** The current state, outside React (tests, event handlers). */
export function groupLiveSnapshot(): LiveState {
  return state;
}

// ─── Receipts ───────────────────────────────────────────────────────────────────────────────────────────────

/** A group's LATEST window landed: its receipts replace what was held (an older page never carries them). */
export function adoptGroupReceipts(conversationId: string, receipts: GroupReceipts | null) {
  if (state.receipts[conversationId] === receipts) return;
  setState({ ...state, receipts: { ...state.receipts, [conversationId]: receipts } });
}

/** A `receipt` frame; false when the held receipts do not list that person (or none are held). */
export function applyReceiptFrame(frame: ReceiptFrame): boolean {
  const held = state.receipts[frame.conversationId];
  const { receipts, known } = mergeReceiptFrame(held, frame);
  if (receipts !== held) setState({ ...state, receipts: { ...state.receipts, [frame.conversationId]: receipts } });
  return known;
}

export function useGroupReceipts(conversationId: string | null | undefined): GroupReceipts | null {
  const get = () => (conversationId ? state.receipts[conversationId] ?? null : null);
  return useSyncExternalStore(subscribe, get, get);
}

// ─── Typing ─────────────────────────────────────────────────────────────────────────────────────────────────

function setTyping(conversationId: string, entries: readonly TypingEntry[]) {
  if (state.typing[conversationId] === entries) return;
  setState({ ...state, typing: { ...state.typing, [conversationId]: entries } });
}

export function applyGroupTypingFrame(frame: TypingFrame, now = Date.now()) {
  setTyping(frame.conversationId, applyTypingFrame(pruneTyping(state.typing[frame.conversationId] ?? EMPTY_TYPING, now), frame, now));
}

/** A message from `author` arrived in that conversation: they are no longer typing (a Maestro: no longer thinking). */
export function dropGroupTyping(conversationId: string, author: MessageAuthor | null | undefined) {
  const held = state.typing[conversationId];
  if (!held) return;
  setTyping(conversationId, dropTypingForAuthor(held, author));
}

/**
 * One conversation's live typers, re-rendering when one expires. `now` is the render's clock, for `typingLine`.
 */
export function useGroupTyping(conversationId: string | null | undefined): { entries: readonly TypingEntry[]; now: number } {
  const get = () => (conversationId ? state.typing[conversationId] ?? EMPTY_TYPING : EMPTY_TYPING);
  const entries = useSyncExternalStore(subscribe, get, get);
  const [, setTick] = useState(0);
  const expiry = nextTypingExpiry(entries);
  useEffect(() => {
    if (expiry == null) return undefined;
    const timer = setTimeout(() => setTick((n) => n + 1), Math.max(0, expiry - Date.now()) + 50);
    return () => clearTimeout(timer);
  }, [expiry]);
  return { entries, now: Date.now() };
}

// ─── Presence ───────────────────────────────────────────────────────────────────────────────────────────────

export function applyGroupPresence(metaSiteId: string, info: PresenceInfo, now = Date.now()) {
  const presence = withPresence(state.presence, metaSiteId, info, now);
  if (presence !== state.presence) setState({ ...state, presence });
}

/** A `members` answer's `members[].presence` (null from an older symphony-bff: nothing is seeded). */
export function seedPresenceFromMembers(members: unknown, now = Date.now()) {
  if (!Array.isArray(members)) return;
  let presence = state.presence;
  for (const raw of members) {
    if (!raw || typeof raw !== 'object') continue;
    const m = raw as Record<string, unknown>;
    const info = parsePresence(m.presence);
    if (typeof m.metaSiteId === 'string' && m.metaSiteId && info && m.you !== true) presence = withPresence(presence, m.metaSiteId, info, now);
  }
  if (presence !== state.presence) setState({ ...state, presence });
}

/** The presence map, and a clock that ticks every 30 s while `active` (so an "online" can expire on screen). */
export function useGroupPresence(active = true): { presence: PresenceMap; now: number } {
  const presence = useSyncExternalStore(subscribe, () => state.presence, () => state.presence);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(timer);
  }, [active]);
  return { presence, now: Date.now() };
}

// ─── Outgoing hints ─────────────────────────────────────────────────────────────────────────────────────────

/** One acker for the page: `viewer-state {deliveredThroughMessageId}`, coalesced per conversation. */
const deliveredAcker = new DeliveredAcker((conversationId, messageId) => groupChatAction('viewer-state', { conversationId, deliveredThroughMessageId: messageId }));

/** This device has the group through `messageId` (a live message from someone else, or a window's newest). */
export function noteGroupDelivered(conversationId: string, messageId: string, createdAt?: string | null) {
  deliveredAcker.note(conversationId, messageId, createdAt);
}

export function sendGroupTyping(conversationId: string, typing: boolean) {
  void groupChatAction('typing', { conversationId, typing });
}

/** The heartbeat. BACKGROUND goes out with `keepalive`, so it survives the page being hidden or closed. */
export function sendGroupPresence(stateName: 'FOREGROUND' | 'BACKGROUND') {
  if (stateName === 'BACKGROUND') groupChatBeacon('presence', { state: stateName });
  else void groupChatAction('presence', { state: stateName });
}
